import EventBus from '@/event-bus';
import _Singleton from '@/tools/singleton';
import jquery from 'jquery';
import urlTool from '@/tools/url';
import Products from '@/products';
import Shop from '@/shop';
import stripHtml from '@/helpers/stripHtml';

import options from '@/ui/ui_ajax-products/products/default-selectors.json';
import defaultSettings from '@/ui/ui_ajax-products/products/default-settings.json';
import methods from '@/ui/ui_ajax-products/methods/index';
import renderStars from '@/ui/ui_ajax-reviews/methods/renderStars';

/**
 * @class
 * @private
 *
 * @description
 * DOM + AjaxProducts
 */
const AjaxProductsDOM = function() {
  const self = this;

  self.options = options;
  self.defaultSettings = defaultSettings;
  self.methods = methods;
  self.renderStars = renderStars;
  self.locale = Shop.config.config.locale;

  document.addEventListener('DOMContentLoaded', function() {
    self._loadProductsListOnEvent();
  });
};

/**
 * Загрузка отдельной категории
 * @method
 * @private
 */
AjaxProductsDOM.prototype._loadProductsListOnEvent = function() {
  const self = this;
  EventBus.subscribe('ui-ajax-products:load-products-list', function(ajaxProductsListNode) {
    const ajaxProductsNode = ajaxProductsListNode.closest(self.options.ajaxProducts.attr);
    if (!ajaxProductsNode) { return false; }

    const templates = self._getTemplates(ajaxProductsNode);

    const noInited = !ajaxProductsListNode.classList.contains(self.defaultSettings.classes.ajaxProductsIsInit);

    if (noInited && templates.preview) {
      self._renderProductsList(ajaxProductsListNode, templates);
    }
  });
};

/**
 * @private
 *
 * @description
 * Получение данных
 */
AjaxProductsDOM.prototype._getCollectionProducts = function(categoryId, options = {}) {
  return new Promise(async resolve => {
    const url = new URL(location.origin + `/collection/${categoryId}.json`);
    const lang = urlTool.getKeyValue('lang') || '';

    if (options.limit) {
      url.searchParams.append('page_size', options.limit);
    }

    if (options.imagesLimit) {
      url.searchParams.append('images_limit', options.imagesLimit);
    }

    if (options.withoutManyVariants) {
      url.searchParams.append('without_many_variants', options.withoutManyVariants);
    }

    url.searchParams.append('lang', lang);

    if (options.imageResizingRules && Array.isArray(options.imageResizingRules)) {
      const baseKey = 'image_resizing_rules[]';

      options.imageResizingRules.forEach((rule) => {
        if (typeof rule === 'object') {
          Object.entries(rule).forEach(([ key, value ]) => {
            url.searchParams.append(`${baseKey}[${key}]`, value);
          });
        }
      });
    }

    const productsData = await fetch(url).then(response => response.json());
    resolve(productsData);
  });
};

/**
 * @private
 *
 * @description
 * Формируем список товаров
 */
AjaxProductsDOM.prototype._renderProductsList = async function(productsListNode, templates) {
  const self = this;

  let options = {};
  const optionsData = productsListNode.dataset[self.options.ajaxProductsList.data];
  if (optionsData) { options = JSON.parse(optionsData); }

  const collectionHandle = options.collection;
  if (!collectionHandle) { return; }

  (async () => {
    self.methods._toggleLoadingState(productsListNode, self.options.ajaxProducts.attr, self.defaultSettings.classes.ajaxProductsIsLoading);

    const categoryData = await self._getCollectionProducts(collectionHandle, {
      limit: options.limit,
      imageResizingRules: options.imageResizingRules,
      withoutManyVariants: options.withoutManyVariants
    });

    try {
      await Products._storage.saveProducts(categoryData.products,
        {
          checkLocale: true,
          withOriginalUpdatedAt: true,
          withoutManyVariants: options.withoutManyVariants
        });
      categoryData.products.forEach(async(item) => {
        productsListNode.appendChild(self._createProduct(item, options, templates));
      });

      const renderedProducts = jquery(productsListNode).find('[data-product-id]');

      renderedProducts.each(function() { Products.initInstance(jquery(this), options.withoutManyVariants); });

      productsListNode.classList.add(self.defaultSettings.classes.ajaxProductsIsInit);
      EventBus.publish('init-products:ui-ajax-products', { productsListNode: productsListNode });
    } catch (err) {
      console.error(err);
    } finally {
      self.methods._toggleLoadingState(productsListNode, self.options.ajaxProducts.attr, self.defaultSettings.classes.ajaxProductsIsLoading);
    }
  })();
};

/**
 * @private
 *
 * @description
 * Формируем товар
 */
AjaxProductsDOM.prototype._createProduct = function(itemData, options, templates) {
  const self = this;

  const productItem = self.methods._getNodeFromTemplate(templates.preview, self.options.ajaxProductsListItem.attr);

  if (!productItem) { return; }

  const link = `/product/${itemData.permalink}`;
  const id = itemData.id;
  const availableProduct = itemData.available;
  const productName = itemData.title;
  const productVariants = itemData.variants;
  const forbidOrderOverExisting = Shop.config.config.forbid_order_over_existing;
  const reviewsAverageRating = itemData.reviews_average_rating_cached;
  const reviewsCount = itemData.reviews_count_cached;

  // Будет true только в том случае, если была передана настройка withoutManyVariants и у товара больше 1 варианта
  const isMoreThanOneVariant = itemData.is_more_than_one_variant || false;

  const productFormNode = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemForm.attr, true);
  const titleNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemTitle.attr);
  const linkNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemLink.attr);
  const favoritesTriggerNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemFavoritesTrigger.attr);
  const compareTriggerNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemCompareTrigger.attr);
  const stickersNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemStickers.attr);
  const shortDescriptionNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemShortDescription.attr);
  const variantsSelectBlockNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemVariantsSelectBlock.attr);
  const variantOptionValuesNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemOptionValues.attr);
  const imgNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemImage.attr);
  const addCartNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemAddCart.attr);
  const priceMinNodes = self.methods._getFieldNodes(productFormNode, self.options.ajaxProductsListItemPriceMin.attr);
  const priceMaxNodes = self.methods._getFieldNodes(productFormNode, self.options.ajaxProductsListItemPriceMax.attr);
  const productUnitNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductUnit.attr);
  const productReviewsRatingNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemReviewsAverageRating.attr);
  const productReviewsCountNodes = self.methods._getFieldNodes(productItem, self.options.ajaxProductsListItemreviewsCount.attr);

  // set id
  productFormNode.setAttribute('data-product-id', id);
  // set withoutManyVariants
  productFormNode.setAttribute('data-product-without-many-variants', isMoreThanOneVariant);

  if (itemData.list_card_mode) {
    productFormNode.setAttribute(
      self.options.ajaxProductsListItemListCardMode.value, itemData.list_card_mode
    );
  }

  if (itemData.list_card_mode_with_variants) {
    productFormNode.setAttribute(
      self.options.ajaxProductsListItemListCardModeWithVariants.value, itemData.list_card_mode_with_variants
    );
  }

  productReviewsRatingNodes.forEach((item) => {
    if (reviewsAverageRating != null) {
      item.innerHTML += self.renderStars(reviewsAverageRating, {
        baseClass: 'icon-star-o',
        activeClass: 'icon-star'
      }).join('');
    }
  });

  if (itemData.reviews_count_cached) {
    productReviewsCountNodes.forEach((item) => {
      item.textContent = stripHtml(reviewsCount);
    });
  }

  self.setSiteSettings(productFormNode);

  // Название
  titleNodes.forEach((item) => {
    item.textContent = stripHtml(productName);
  });

  // Ссылка перехода в карточку товара
  linkNodes.forEach((item) => {
    item.setAttribute('href', link);
  });

  // Фото товара
  if (options.switchImages == 'true' && itemData.images.length > 1) {
    const raw = options.switchImagesOnHoverLimit;
    const limit = typeof raw === 'number' ? raw : parseInt(raw, 10);
    const imgLimit = Number.isInteger(limit) && limit > 0 ? limit : 2;
    const imgs = itemData.images.slice(0, imgLimit);

    imgNodes.forEach((item) => {
      imgs.forEach((image, index) => {
        const productPicture = self.methods._getNodeFromTemplate(templates.previewImage, this.options.ajaxProductsListItemPicture.attr);
        item.appendChild(self._createImage(productPicture, image, image.title || productName, index + 1));
      });
    });
  } else {
    imgNodes.forEach((item) => {
      const firstImage = itemData.first_image;
      const productPicture = self.methods._getNodeFromTemplate(templates.previewImage, this.options.ajaxProductsListItemPicture.attr);
      item.appendChild(self._createImage(productPicture, firstImage, firstImage.title || productName));
    });
  }

  // Формирование select-вариантов товара
  if (productVariants.length && variantsSelectBlockNodes.length && !isMoreThanOneVariant) {
    variantsSelectBlockNodes.forEach(variantsSelectBlockNode => {
      self.methods
        ._createVariantsSelector(
          productVariants, variantsSelectBlockNode, options, self.options.ajaxProductsListItemVariantsSelectBlock.data
        );
    });
  }

  // Вывод названий свойств вариантов в виде картинок или в виде текста
  if (variantOptionValuesNodes.length && productVariants.length && !isMoreThanOneVariant) {
    const idSet = new Set();
    const uniqueOptionValues = [];

    const hideItemsOutOfStock = Shop.config.get('hide_items_out_of_stock').hide_items_out_of_stock;

    const filteredProductVariants = productVariants.filter(variant => !(hideItemsOutOfStock && variant.quantity === 0));

    // Достаём уникальные option_values и кладём их в отдельный массив
    for (let variant of filteredProductVariants) {
      for (let option of variant.option_values) {
        if (!idSet.has(option.id)) {
          idSet.add(option.id);
          uniqueOptionValues.push(option);
        }
      }
    }

    const createOptionValuesData = {
      variantOptionValuesNodes,
      uniqueOptionValues,
      optionNames: itemData.option_names,
      productVariants: filteredProductVariants,
      images: itemData.images,
      imageTemplate: templates.previewOptionValuesImage,
      textTemplate: templates.previewOptionValuesText
    };

    self._createOptionValues(createOptionValuesData);
  }

  // Стикеры
  if (itemData.properties.length > 0) {
    const markId = itemData.properties.find(
      (item) => item.permalink == 'label'
    );

    if (markId && itemData.characteristics) {
      itemData.characteristics.forEach((characteristicsItem) => {
        if (characteristicsItem.property_id == markId.id) {
          stickersNodes.forEach((item) => {
            item.innerHTML += self.methods._renderLabel(characteristicsItem.permalink, characteristicsItem.title);
          });
        }
      });
    }
  }

  // Краткое описание
  if (itemData.short_description) {
    let text = stripHtml(itemData.short_description);

    if (options.shortDescriptionWordsCount) {
      const maxWords = parseInt(options.shortDescriptionWordsCount);
      let textArr = text.split(' ');

      text = textArr.slice(0, maxWords).join(' ');

      if (textArr.length > maxWords) {
        text += '...';
      }
    }

    if (text && text !== '') {
      shortDescriptionNodes.forEach((item) => {
        item.innerHTML = text;
      });
    }
  }

  // Кнопка добавления в избранное
  favoritesTriggerNodes.forEach((item) => {
    item.setAttribute('data-ui-favorites-trigger', id);
  });

  // Кнопка добавления в сравнение
  compareTriggerNodes.forEach((item) => {
    item.setAttribute('data-compare-trigger', id);
  });

  // Единица измерения товара
  if (productUnitNodes.length) {
    productUnitNodes.forEach(item => {
      item.innerHTML += `${Shop.units.getName(itemData.unit)}`;
    });
  }

  // Удаление кнопки добавления в корзину
  if (!availableProduct && forbidOrderOverExisting) {
    addCartNodes.forEach((item) => {
      item.remove();
    });
  }

  // Вашаем различные классы на элемент формы карточки товара
  self.methods._checkIfHasManyProductVariants(productFormNode, productVariants, self.defaultSettings.classes.ajaxProductsListItemHasManyVariants, isMoreThanOneVariant);
  self.methods._checkIfProductHasManyPrices(productFormNode, itemData, self.defaultSettings.classes.ajaxProductsListItemHasManySalePrices, self.defaultSettings.classes.ajaxProductsListItemHasManyOldPrices);

  // Выводим максимальную и минимальную цены
  if (priceMinNodes.length && itemData.price_min) {
    priceMinNodes.forEach(priceMinNode => {
      const hasZeroPriceTextAttr = priceMinNode.hasAttribute(self.options.ajaxProductsListItemZeroPriceText.value);

      const zeroPriceText = (
        priceMinNode.dataset[self.options.ajaxProductsListItemZeroPriceText.data]
        || self._getMessageByLocale('zeroPriceText')
      );

      if (parseFloat(itemData.price_min) === 0 && hasZeroPriceTextAttr) {
        priceMinNode.innerHTML = zeroPriceText;
        productFormNode.classList.toggle(self.defaultSettings.classes.ajaxProductsListItemHasZeroMinPrice);

        return;
      }

      priceMinNode.innerHTML = Shop.money.format(itemData.price_min);
    });
  }

  if (priceMaxNodes.length && itemData.price_max) {
    priceMaxNodes.forEach(priceMax => {
      priceMax.innerHTML = Shop.money.format(itemData.price_max);
    });
  }

  if (itemData.bundle) {
    productFormNode.classList.add(self.defaultSettings.classes.ajaxProductsListItemIsBundle);
  }

  return productItem;
};

/**
 * @private
 *
 * @description
 * Получение текста из словаря базовых сообщений
 *
 * @param {String} messageName - название сообщения
 * @return {String} текст сообщения
 */
AjaxProductsDOM.prototype._getMessageByLocale = function(messageName) {
  const self = this;

  if (typeof self.locale !== 'undefined') {
    let result_message = self.defaultSettings[messageName][self.locale];

    if (typeof result_message === 'undefined') {
      result_message = self.defaultSettings[messageName].ru;
    }

    return result_message;
  }

  return self.defaultSettings[messageName].ru;
};

/**
 * @private
 *
 * @description
 * Рендерим названия свойств в виде картинок и/или текста
 */
AjaxProductsDOM.prototype._createOptionValues = function(methodData) {
  const self = this;

  // Получаем переданные параметры
  const variantOptionValuesNodes = methodData.variantOptionValuesNodes;
  const uniqueOptionValues = methodData.uniqueOptionValues;
  const optionNames = methodData.optionNames;
  const productVariants = methodData.productVariants;
  const images = methodData.images;
  const imageTemplate = methodData.imageTemplate;
  const textTemplate = methodData.textTemplate;

  // Перебираем каждую ноду где нужно вывести значения свойств
  variantOptionValuesNodes.forEach(variantOptionValuesNode => {
    // Создаём флаг для проверки, что хотя бы одно значение свойства было выведено
    let status = false;

    // Получаем настройки из data-атрибута
    let optionValuesSettings = {};
    const optionValuesData = variantOptionValuesNode.dataset[self.options.ajaxProductsListItemOptionValues.data];
    if (optionValuesData) {
      optionValuesSettings = JSON.parse(optionValuesData);
    }

    // Получаем ноду для вставки картинок
    const optionValuesImagesNode = self.methods._getFieldNodes(variantOptionValuesNode, self.options.ajaxProductsListItemOptionValuesImages.attr, true);

    // Проверяем, есть ли у товара имя свойства совпадающее с переданным в настройках
    const imagesOptionNames = optionNames.filter(item => optionValuesSettings.imagesOptionNames.includes(item.title.toLowerCase()));

    // Определяем свойство с минимальной позицией в товаре
    let minPosition = Math.min(...optionNames.map(item => item.position));

    // Если свойство с картинками имеет минимальную позицию, то выводим его первым, если нет, то последним (задаём класс)
    if (imagesOptionNames.length && imagesOptionNames[0].position === minPosition) {
      variantOptionValuesNode.classList.add(self.defaultSettings.classes.ajaxProductsListItemOptionImagesValuesFirst);
    } else {
      variantOptionValuesNode.classList.add(self.defaultSettings.classes.ajaxProductsListItemOptionImagesValuesLast);
    }

    // Получаем значения свойств, у которых могут/могли быть изображения
    const imagesOptionNamesIds = imagesOptionNames.map(name => name.id);
    const imagesOptionValues = uniqueOptionValues.filter(value => imagesOptionNamesIds.includes(value.option_name_id));

    // Получаем ID изображений у вариантов, если таковые есть, чистим дублирующиеся ID
    const variantImagesIds = [ ...new Set(productVariants.map(item => item.image_id).filter(item => item)) ];

    if (imagesOptionNames.length && optionValuesSettings.showVariantsText !== 'true' && variantImagesIds.length > 1) {
      // Достаём изображения вариантов соответствующие названию переданного свойства и если такие есть, то выводим их в блоке для картинок
      const valuesImages = images.filter(image => variantImagesIds.includes(image.id));

      // Получаем отсортированный массив с изображениями, чтобы соблюдался порядок значений свойств заданный в бэк-офисе
      const idToIndex = {};
      variantImagesIds.forEach((id, index) => {
        idToIndex[id] = index;
      });

      const sortedValuesImages = valuesImages.slice().sort((a, b) => {
        return idToIndex[a.id] - idToIndex[b.id];
      });

      // Если есть картинки то выводим их, если нет, то выводим значения свойств в виде текста
      if (optionValuesImagesNode && sortedValuesImages.length) {
        status = true;

        sortedValuesImages.forEach(image => {
          const imagesItemNode = self.methods._getNodeFromTemplate(imageTemplate, self.options.ajaxProductsListItemOptionValuesItemImagesItem.attr);

          let imageNode = self.methods._getFieldNodes(imagesItemNode, self.options.ajaxProductsListItemOptionValuesItemImagesItemImg.attr, true);

          if (imageNode) {
            imageNode.src = image.medium_url;
            optionValuesImagesNode.appendChild(imagesItemNode);
          }
        });
      }
    } else if (imagesOptionValues.length) {
      status = true;
      // Если картинок не оказалось, то создаём блок для текстовых значений и выводим их как текстовые
      const textOptionValuesListNode = document.createElement('DIV');
      textOptionValuesListNode.classList.add(self.defaultSettings.classes.ajaxProductsListItemOptionListValues);
      imagesOptionValues.forEach(imageValue => {
        let textValueNode = self.methods._getNodeFromTemplate(textTemplate, self.options.ajaxProductsListItemOptionValuesItemText.attr);

        if (textValueNode) {
          textValueNode.textContent = imageValue.title;

          if (textValueNode.hasAttribute(self.options.ajaxProductsListItemOptionValuesItemId.value)) {
            textValueNode.dataset[self.options.ajaxProductsListItemOptionValuesItemId.data] = imageValue.id;
          }
        }

        textOptionValuesListNode.appendChild(textValueNode);
      });

      variantOptionValuesNode.appendChild(textOptionValuesListNode);
      textOptionValuesListNode.classList.add(self.defaultSettings.classes.ajaxProductsListItemOptionListValuesColors);
    }

    // Достаём значения без картинок, создаём блок для их вывода, вставляем значения в созданный блок, а затем добавляем блок в документ
    const textOptionNames = optionNames.filter(item => !optionValuesSettings.imagesOptionNames.includes(item.title.toLowerCase()));
    const textOptionNamesIds = textOptionNames.map(name => name.id);

    const textOptionValuesArrays = [];

    textOptionNamesIds.forEach(itemID => {
      textOptionValuesArrays.push(uniqueOptionValues.filter(value => value.option_name_id === itemID));
    });

    if (textOptionValuesArrays.length) {
      status = true;
      textOptionValuesArrays.forEach(textOptionValues => {
        const textOptionValuesListNode = document.createElement('DIV');
        textOptionValuesListNode.classList.add(self.defaultSettings.classes.ajaxProductsListItemOptionListValues);

        textOptionValues.forEach(value => {
          let textValueNode = self.methods._getNodeFromTemplate(textTemplate, self.options.ajaxProductsListItemOptionValuesItemText.attr);

          if (textValueNode) {
            textValueNode.textContent = value.title;

            if (textValueNode.hasAttribute(self.options.ajaxProductsListItemOptionValuesItemId.value)) {
              textValueNode.dataset[self.options.ajaxProductsListItemOptionValuesItemId.data] = value.id;
            }
          }

          textOptionValuesListNode.appendChild(textValueNode);
        });

        variantOptionValuesNode.appendChild(textOptionValuesListNode);
      });
    }

    if (!status) {
      variantOptionValuesNode.style.display = 'none';
    }
  });
};

/**
 * @private
 *
 * @description
 * Загружаем картинки
 */
AjaxProductsDOM.prototype._createImage = function(productPicture, image, title = '', index) {
  const self = this;

  const pictureSources = self.methods._getFieldNodes(productPicture, 'source');
  const pictureImg = productPicture.querySelector('img');

  pictureSources.forEach((item) => {
    const url = self.methods._imageUrlForPictureSource(
      image,
      item,
      self.options.ajaxProductsImageResizingRulesIndex.data
    );

    // Плавно переходим на нативный lazyload, условие нужно чтобы ничего не сломать в кастомных виджетах
    if (item.classList.contains('lazyload') && typeof LazyLoad === 'function') {
      item.setAttribute('data-srcset', url);
    } else {
      item.setAttribute('srcset', url);
    }
  });

  if (pictureImg) {
    const url = self.methods._imageUrlForPictureSource(
      image,
      pictureImg,
      self.options.ajaxProductsImageResizingRulesIndex.data
    );

    let classForSwitch = null;
    if (index) { classForSwitch = `product-preview__img-${index}`; }

    // Плавно переходим на нативный lazyload, условие нужно чтобы ничего не сломать в кастомных виджетах
    if (pictureImg.classList.contains('lazyload') && typeof LazyLoad === 'function') {
      pictureImg.setAttribute('data-src', url);
    } else {
      pictureImg.setAttribute('src', url);
    }

    pictureImg.setAttribute('alt', title);
    pictureImg.setAttribute('title', title);
    if (classForSwitch) {
      pictureImg.classList.add(classForSwitch);
    }
  }

  return productPicture;
};

AjaxProductsDOM.prototype._getTemplates = function(ajaxProductsNode) {
  const self = this;

  const templates = {};

  templates.preview = ajaxProductsNode.querySelector(self.options.ajaxProductsListItemTemplate.attr);
  templates.previewImage = ajaxProductsNode.querySelector(self.options.ajaxProductsListItemImageTemplate.attr);
  templates.previewOptionValuesImage = ajaxProductsNode.querySelector(self.options.ajaxProductsListItemOptionValuesImagesItemTemplate.attr);
  templates.previewOptionValuesText = ajaxProductsNode.querySelector(self.options.ajaxProductsListItemOptionValuesItemTextTemplate.attr);

  return templates;
};

/**
 * Установка настроек сайта
 * @method
 * @private
 * @param {HTMLElement} productForm - Нода формы товара
 *
 * @return {void}
 *
 * @todo В будущем мы планируем сделать, чтобы эти настройки прилетали для каждого товара индивиудально. Пока это не реализовано мы берём настройки из Shop.config.config.site_setting.
 */
AjaxProductsDOM.prototype.setSiteSettings = function(productForm) {
  const self = this;

  const siteSettings = window.Shop.config.config.site_setting;

  if (!siteSettings) { return; }

  if (siteSettings.show_quick_checkout_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductsListItemShowQuickCheckoutButton);
  }

  if (siteSettings.show_marketplace_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductsListItemShowMarketplaceButton);
  }

  if (siteSettings.show_service_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductsListItemShowServiceButton);
  }

  if (siteSettings.show_cart_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductsListItemShowCartButton);
  }

  if (!siteSettings.show_quick_checkout_button && !siteSettings.show_marketplace_button && !siteSettings.show_service_button && !siteSettings.show_cart_button) {
    productForm.classList.add(self.defaultSettings.classes.ajaxProductsListItemHideAllButtons);
  }
};

export default _Singleton(AjaxProductsDOM).getInstance();
